import { Fragment, useState } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { XMarkIcon, CalendarIcon, ClockIcon } from '@heroicons/react/24/outline'
import { useTranslation } from 'react-i18next'
import { updateProjectDeletion } from '../../Requests/ProjectRequests'
import Loader from '../Loader'

export default function ProjectDeletionDateSlideover({ project, isOpen, onClose, onSuccess }) {
    const { t } = useTranslation()
    const [customDate, setCustomDate] = useState('')
    const [customMonths, setCustomMonths] = useState(6)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)

    const handleExtend = async (months = 6) => {
        try {
            setLoading(true)
            setError(null)
            const response = await updateProjectDeletion(project.uuid, { 
                action: 'extend', 
                months: months 
            })
            if (onSuccess) {
                onSuccess(response.data.project)
            }
            onClose()
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to extend deletion date')
        } finally {
            setLoading(false)
        }
    }

    const handleSetCustomDate = async () => {
        if (!customDate) {
            setError(t('ProjectDeletion.ErrorSelectDate'))
            return
        }
        
        const selectedDate = new Date(customDate)
        const now = new Date()
        
        if (selectedDate <= now) {
            setError(t('ProjectDeletion.ErrorFutureDate'))
            return
        }

        try {
            setLoading(true)
            setError(null)
            const response = await updateProjectDeletion(project.uuid, {
                action: 'set_date',
                scheduled_deletion_at: selectedDate.toISOString()
            })
            if (onSuccess) {
                onSuccess(response.data.project)
            }
            onClose()
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to set deletion date')
        } finally {
            setLoading(false)
        }
    }

    const handleExtendCustomMonths = async () => {
        if (customMonths < 1 || customMonths > 24) {
            setError(t('ProjectDeletion.ErrorMonthsRange'))
            return
        }
        await handleExtend(customMonths)
    }

    const resetForm = () => {
        setCustomDate('')
        setCustomMonths(6)
        setError(null)
        setLoading(false)
    }

    const handleClose = () => {
        resetForm()
        onClose()
    }

    return (
        <Transition.Root show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-10" onClose={handleClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-in-out duration-500"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in-out duration-500"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-hidden">
                    <div className="absolute inset-0 overflow-hidden">
                        <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
                            <Transition.Child
                                as={Fragment}
                                enter="transform transition ease-in-out duration-500 sm:duration-700"
                                enterFrom="translate-x-full"
                                enterTo="translate-x-0"
                                leave="transform transition ease-in-out duration-500 sm:duration-700"
                                leaveFrom="translate-x-0"
                                leaveTo="translate-x-full"
                            >
                                <Dialog.Panel className="pointer-events-auto w-screen max-w-md">
                                    <div className="flex h-full flex-col overflow-y-scroll bg-white py-6 shadow-xl rounded-l-xl">
                                        <div className="px-4 sm:px-6">
                                            <div className="flex items-start justify-between">
                                                <Dialog.Title className="text-base font-semibold leading-6 text-gray-900">
                                                    {t('ProjectDeletion.SlideoverTitle')}
                                                </Dialog.Title>
                                                <div className="ml-3 flex h-7 items-center">
                                                    <button
                                                        type="button"
                                                        className="relative rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2"
                                                        onClick={handleClose}
                                                    >
                                                        <span className="absolute -inset-2.5" />
                                                        <span className="sr-only">{t('Common.Close')}</span>
                                                        <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                                                    </button>
                                                </div>
                                            </div>
                                            <p className="mt-1 text-sm text-gray-500">
                                                {t('ProjectDeletion.SlideoverDescription', { projectName: project?.name })}
                                            </p>
                                        </div>
                                        <div className="relative mt-6 flex-1 px-4 sm:px-6 flex flex-col justify-between">
                                            <div className="flex flex-col space-y-6">
                                                {error && (
                                                    <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                                                        <span className="text-sm text-red-800">{error}</span>
                                                        <button 
                                                            onClick={() => setError(null)}
                                                            className="ml-2 text-sm text-red-600 underline hover:text-red-800"
                                                        >
                                                            {t('Common.Dismiss')}
                                                        </button>
                                                    </div>
                                                )}

                                                {/* Quick extend options */}
                                                <div>
                                                    <h3 className="text-sm font-medium text-gray-900 mb-3">{t('ProjectDeletion.QuickExtensions')}</h3>
                                                    <div className="space-y-2">
                                                        <button
                                                            onClick={() => handleExtend(1)}
                                                            disabled={loading}
                                                            className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 text-sm"
                                                        >
                                                            <ClockIcon className="h-4 w-4 mr-2" />
                                                            {loading ? <Loader /> : t('ProjectDeletion.Extend1Month')}
                                                        </button>
                                                        <button
                                                            onClick={() => handleExtend(3)}
                                                            disabled={loading}
                                                            className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 text-sm"
                                                        >
                                                            <ClockIcon className="h-4 w-4 mr-2" />
                                                            {loading ? <Loader /> : t('ProjectDeletion.Extend3Months')}
                                                        </button>
                                                        <button
                                                            onClick={() => handleExtend(6)}
                                                            disabled={loading}
                                                            className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 text-sm"
                                                        >
                                                            <ClockIcon className="h-4 w-4 mr-2" />
                                                            {loading ? <Loader /> : t('ProjectDeletion.Extend6Months')}
                                                        </button>
                                                        <button
                                                            onClick={() => handleExtend(12)}
                                                            disabled={loading}
                                                            className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 text-sm"
                                                        >
                                                            <ClockIcon className="h-4 w-4 mr-2" />
                                                            {loading ? <Loader /> : t('ProjectDeletion.Extend1Year')}
                                                        </button>
                                                    </div>
                                                </div>

                                                {/* Custom months */}
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-900 mb-2">
                                                        {t('ProjectDeletion.CustomExtension')}
                                                    </label>
                                                    <div className="flex items-center space-x-2">
                                                        <input
                                                            type="number"
                                                            min="1"
                                                            max="24"
                                                            value={customMonths}
                                                            onChange={(e) => setCustomMonths(parseInt(e.target.value) || 1)}
                                                            className="w-20 px-3 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-black rounded-md text-sm"
                                                        />
                                                        <span className="text-sm text-gray-600">{t('ProjectDeletion.Months')}</span>
                                                        <button
                                                            onClick={handleExtendCustomMonths}
                                                            disabled={loading}
                                                            className="flex-1 bg-black text-white rounded-md px-3 py-1.5 text-sm hover:bg-gray-800 disabled:opacity-50"
                                                        >
                                                            {loading ? <Loader /> : t('ProjectDeletion.ExtendButton')}
                                                        </button>
                                                    </div>
                                                </div>

                                                {/* Custom date */}
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-900 mb-2">
                                                        {t('ProjectDeletion.SetSpecificDate')}
                                                    </label>
                                                    <input
                                                        type="datetime-local"
                                                        value={customDate}
                                                        onChange={(e) => setCustomDate(e.target.value)}
                                                        min={new Date().toISOString().slice(0, 16)}
                                                        className="w-full px-3 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-black rounded-md text-sm mb-2"
                                                    />
                                                </div>
                                            </div>
                                            
                                            <div className="flex justify-end mt-6">
                                                {loading ? 
                                                    <div className='bg-black text-white rounded-md px-12 py-2 w-fit'>
                                                        <Loader />
                                                    </div> 
                                                    : 
                                                    <button
                                                        onClick={handleSetCustomDate}
                                                        disabled={!customDate}
                                                        className="bg-black text-white rounded-md px-12 py-2 w-fit hover:bg-gray-800 disabled:opacity-50"
                                                    >
                                                        {t('ProjectDeletion.SetDateButton')}
                                                    </button>
                                                }
                                            </div>
                                        </div>
                                    </div>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    )
}